/**
 * Getting a Stytch session from the phone app instead of the login page.
 *
 * The embedded console cannot sign in by itself: Google blocks OAuth in a
 * WebView and magic links open Safari. The app mints a session for one team
 * org at a time (`POST /auth/console-session`, see collabv3) and hands it over
 * through the bridge. Minted sessions cannot `session.exchange`, so moving to a
 * different org, or recovering an expired session, is always a new request to
 * the app, never an exchange.
 *
 * Every request carries a timeout and settles exactly once, so a host that
 * never answers shows a visible failure instead of a spinner.
 */

import { postToNative } from './bridge';

export const NATIVE_SESSION_TIMEOUT_MS = 20_000;

export interface NativeSessionTokens {
  orgId: string;
  sessionToken: string;
  sessionJwt: string;
}

/** Codes the app reports; `timeout` and `no-host` are produced here. */
export type NativeSessionErrorCode =
  | 'org_auth_required'
  | 'not_a_member'
  | 'unavailable'
  | 'failed'
  | 'timeout'
  | 'no-host';

export class NativeSessionError extends Error {
  readonly code: NativeSessionErrorCode;
  readonly orgId: string | null;
  readonly reason: string | null;

  constructor(code: NativeSessionErrorCode, orgId: string | null, reason: string | null = null) {
    super(nativeSessionErrorMessage(code));
    this.name = 'NativeSessionError';
    this.code = code;
    this.orgId = orgId;
    this.reason = reason;
  }
}

export function nativeSessionErrorMessage(code: NativeSessionErrorCode): string {
  switch (code) {
    case 'org_auth_required': return 'This team requires additional sign-in. Open it in Safari to continue.';
    case 'not_a_member': return 'You are not a member of this team.';
    case 'timeout': return 'The app did not answer the sign-in request in time.';
    case 'no-host': return 'This page needs the Nimbalyst app to sign in.';
    default: return 'The app could not sign this page in.';
  }
}

interface PendingRequest {
  orgId: string | null;
  resolve(tokens: NativeSessionTokens): void;
  reject(error: NativeSessionError): void;
  timer: ReturnType<typeof setTimeout>;
}

const pending = new Map<string, PendingRequest>();
const inFlightByOrg = new Map<string, Promise<NativeSessionTokens>>();

const DELIVERY_ERRORS = new Set(['org_auth_required', 'not_a_member', 'unavailable', 'failed']);

function newRequestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `req-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Ask the app for a session. `orgId` null means "the session expired and this
 * page does not know which org it was for"; the app picks the org it opened.
 * Concurrent requests for the same org share one round trip.
 */
export function requestNativeSession(
  orgId: string | null,
  reason: 'switch-org' | 'expired',
  timeoutMs = NATIVE_SESSION_TIMEOUT_MS,
): Promise<NativeSessionTokens> {
  const key = `${reason}:${orgId ?? ''}`;
  const existing = inFlightByOrg.get(key);
  if (existing) return existing;

  const requestId = newRequestId();
  const request = new Promise<NativeSessionTokens>((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(requestId);
      reject(new NativeSessionError('timeout', orgId));
    }, timeoutMs);
    pending.set(requestId, { orgId, resolve, reject, timer });
    const sent = reason === 'expired'
      ? postToNative({ type: 'sessionExpired', requestId, orgId })
      : orgId
        ? postToNative({ type: 'requestSession', requestId, orgId })
        : false;
    if (!sent) {
      clearTimeout(timer);
      pending.delete(requestId);
      reject(new NativeSessionError('no-host', orgId));
    }
  });
  inFlightByOrg.set(key, request);
  void request.then(() => inFlightByOrg.delete(key), () => inFlightByOrg.delete(key));
  return request;
}

/**
 * The app's answer to `requestSession` / `sessionExpired`. Returns whether the
 * payload settled a pending request, so the app can log a stale delivery.
 */
export function deliverNativeSession(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const record = payload as Record<string, unknown>;
  const requestId = typeof record.requestId === 'string' ? record.requestId : null;
  const request = requestId ? pending.get(requestId) : undefined;
  if (!requestId || !request) return false;
  pending.delete(requestId);
  clearTimeout(request.timer);

  const deliveredOrgId = typeof record.orgId === 'string' ? record.orgId : null;
  if (typeof record.error === 'string') {
    const code = (DELIVERY_ERRORS.has(record.error) ? record.error : 'failed') as NativeSessionErrorCode;
    const reason = typeof record.reason === 'string' ? record.reason : null;
    const orgId = deliveredOrgId ?? request.orgId;
    if (code === 'org_auth_required' && orgId) postToNative({ type: 'orgAuthRequired', orgId, reason });
    request.reject(new NativeSessionError(code, orgId, reason));
    return true;
  }

  const { sessionToken, sessionJwt } = record;
  if (
    typeof sessionToken !== 'string' || !sessionToken
    || typeof sessionJwt !== 'string' || !sessionJwt
    || !deliveredOrgId
    || (request.orgId !== null && deliveredOrgId !== request.orgId)
  ) {
    request.reject(new NativeSessionError('failed', request.orgId, 'malformed-delivery'));
    return true;
  }
  request.resolve({ orgId: deliveredOrgId, sessionToken, sessionJwt });
  return true;
}

export interface HydratableSessionClient {
  updateSession(tokens: { session_token: string; session_jwt: string }): void;
  authenticate(options: { session_duration_minutes: number }): Promise<{ session_jwt?: string | null }>;
}

/**
 * Adopt delivered tokens in the Stytch SDK: `updateSession` writes the session
 * cookies, `authenticate` loads the member and the real expiry. Returns the
 * JWT the SDK now holds.
 */
export async function hydrateNativeSession(
  client: HydratableSessionClient,
  tokens: NativeSessionTokens,
  sessionDurationMinutes: number,
): Promise<string> {
  client.updateSession({ session_token: tokens.sessionToken, session_jwt: tokens.sessionJwt });
  const result = await client.authenticate({ session_duration_minutes: sessionDurationMinutes });
  if (typeof result.session_jwt !== 'string' || !result.session_jwt) {
    throw new NativeSessionError('failed', tokens.orgId, 'authenticate-returned-no-session');
  }
  return result.session_jwt;
}

/** Tests only. */
export function resetNativeSessionRequestsForTests(): void {
  for (const request of pending.values()) clearTimeout(request.timer);
  pending.clear();
  inFlightByOrg.clear();
}
